import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  Polyline,
  Circle,
  useJsApiLoader,
  TransitLayer,
} from "@react-google-maps/api";
import { toast } from "react-toastify";
import {
  getData,
  postDataWithBody,
  putDataWithBody,
} from "../../../../Services/Services";
import {
  getThemeURL,
  travelendtripapi,
  travelstarttripapi,
  getAssignedWaypoints,
  fetchVehicleTracking,
} from "../../../../Services/EndPoints";
import "./TripActionStyles.css";

// ─── Constants (defined OUTSIDE component so they never re-create) ─────────────
// Shared loader config — MUST match every other component using useJsApiLoader
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_LOADER_ID,
} from "../Googlemapsloader";

const MAP_CONTAINER_STYLE = { width: "100%", height: "100%", borderRadius: "16px" };
const DEFAULT_CENTER = { lat: 17.4435, lng: 78.3485 };

const BASE_MAP_OPTIONS = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: true,
  mapTypeControl: false,
  fullscreenControl: true,
  gestureHandling: "greedy",
};

// ─── Map layer config (matches RouteList) ─────────────────────────────────────
const mapTypes = {
  road: "roadmap",
  satellite: "satellite",
  terrain: "terrain",
  hybrid: "hybrid",
};

const LAYERS = [
  {
    id: "road", label: "Standard",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/></svg>,
  },
  {
    id: "satellite", label: "Satellite",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
  },
  {
    id: "terrain", label: "Terrain",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 18 9 6 13 13 16 9 21 18"/></svg>,
  },
  {
    id: "hybrid", label: "Hybrid",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><line x1="2" y1="12" x2="22" y2="12"/></svg>,
  },
];

// ─── SVG Icon helpers (matching RouteList style) ───────────────────────────────
const makePinSvg = (fillColor) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="44" viewBox="0 0 28 44">
    <path d="M14 0C6.268 0 0 6.268 0 14c0 5.817 3.412 10.83 8.35 13.269L14 44l5.65-16.731C24.588 24.83 28 19.817 28 14 28 6.268 21.732 0 14 0z"
      fill="${fillColor}" stroke="white" stroke-width="1.5"/>
    <circle cx="14" cy="14" r="5.5" fill="white" fill-opacity="0.85"/>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const makeHaltSvg = () => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34">
    <circle cx="17" cy="17" r="16" fill="#FBBC04" stroke="white" stroke-width="2.5"/>
    <rect x="11" y="10" width="4" height="14" rx="1.5" fill="white"/>
    <rect x="19" y="10" width="4" height="14" rx="1.5" fill="white"/>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

/** Safe icon factory — only call once window.google is available */
const pinIcon = (type) => {
  if (!window.google) return undefined;
  const fillColor = type === "green" ? "#34A853" : "#EA4335";
  return {
    url: makePinSvg(fillColor),
    scaledSize: new window.google.maps.Size(28, 44),
    anchor: new window.google.maps.Point(14, 44),
  };
};

const haltIcon = () => {
  if (!window.google) return undefined;
  return {
    url: makeHaltSvg(),
    scaledSize: new window.google.maps.Size(34, 34),
    anchor: new window.google.maps.Point(17, 17),
  };
};

// Compass bearing in degrees (0 = north) between two {lat,lng} coords
const calcBearing = (from, to) => {
  const toRad = d => d * Math.PI / 180;
  const lat1 = toRad(from.lat), lat2 = toRad(to.lat);
  const dLng = toRad(to.lng - from.lng);
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
};

const VEHICLE_EMOJI = {
  car: '🚗',        sedan: '🚗',
  bus: '🚌',        minibus: '🚌',
  truck: '🚛',      lorry: '🚛',
  bike: '🏍️',       bicycle: '🚲', motorcycle: '🏍️', motorbike: '🏍️',
  van: '🚐',
  suv: '🚙',
  autorickshaw: '🛺', auto: '🛺', rickshaw: '🛺',
  ambulance: '🚑',
  firetruck: '🚒',  firetruck: '🚒',
  police: '🚔',     policecars: '🚔',
  taxi: '🚕',
  tractor: '🚜',
};

const getVehicleIconSvg = (vehicleType, color1, color2, heading = 0) => {
  const type  = (vehicleType || '').toLowerCase().replace(/[\s_-]+/g, '');
  const emoji = VEHICLE_EMOJI[type] || '🚘';
  const deg   = Math.round(heading);

  return `<svg xmlns="http://www.w3.org/2000/svg" width="52" height="52" viewBox="0 0 52 52">
    <defs>
      <radialGradient id="g" cx="50%" cy="40%">
        <stop offset="0%" stop-color="${color1}"/>
        <stop offset="100%" stop-color="${color2}"/>
      </radialGradient>
      <filter id="sh" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="rgba(0,0,0,0.32)"/>
      </filter>
    </defs>
    <circle cx="26" cy="26" r="22" fill="url(#g)" stroke="white" stroke-width="3" filter="url(#sh)"/>
    <text x="26" y="31" font-size="20" text-anchor="middle"
          font-family="Segoe UI Emoji,Apple Color Emoji,Noto Color Emoji,sans-serif">${emoji}</text>
    <g transform="rotate(${deg},26,26)">
      <polygon points="26,4 30,13 26,11 22,13" fill="white" opacity="0.95"/>
    </g>
  </svg>`;

};

const liveVehicleIcon = (isDynamic, status, vehicleType, heading = 0) => {
  // status: 'moving' | 'just-stopped' | 'idle' | 'offline'
  let color1, color2;
  switch (status) {
    case 'just-stopped': color1 = "#dc3545"; color2 = "#b02a37"; break;
    case 'idle':         color1 = "#ffc107"; color2 = "#e0a800"; break;
    case 'offline':      color1 = "#9e9e9e"; color2 = "#757575"; break;
    default:             color1 = "#28a745"; color2 = "#1e7e34"; break;
  }
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
      getVehicleIconSvg(vehicleType, color1, color2, heading)
    )}`,
    scaledSize: new window.google.maps.Size(52, 52),
    anchor: new window.google.maps.Point(26, 26),
  };
};

// ─── Confirmation Modal ────────────────────────────────────────────────────────
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, confirmText, type, isLoading }) => {
  if (!isOpen) return null;

  const btnGradient =
    type === "start" ? "linear-gradient(135deg,#10b981,#059669)"
      : type === "end" ? "linear-gradient(135deg,#ef4444,#dc2626)"
        : "linear-gradient(135deg,#1a7a73,#1a7a73)";

  const iconBg =
    type === "start" ? "linear-gradient(135deg,#d1fae5,#a7f3d0)"
      : type === "end" ? "linear-gradient(135deg,#fee2e2,#fecaca)"
        : "linear-gradient(135deg,#ccfbf1,#99f6e4)";

  return (
    <div className="modal-overlay-custom">
      <div className="modal-content-enhanced">
        <div className="modal-header-enhanced">
          <div className="modal-icon-enhanced" style={{ background: iconBg }}>
            {type === "start" ? "🚀" : type === "end" ? "🏁" : "❓"}
          </div>
          <h3 className="modal-title-enhanced">{title}</h3>
        </div>
        <p className="modal-message-enhanced">{message}</p>
        <div className="modal-actions-enhanced">
          <button onClick={onClose} disabled={isLoading} className="btn-cancel-enhanced">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={isLoading}
            className="btn-confirm-enhanced" style={{ background: btnGradient }}>
            {isLoading && (
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
            )}
            {isLoading ? "Processing…" : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
const TripAction = () => {
  const location = useLocation();
  const { data, vehicleType: _vehicleType, vehicleData: vehicleInfo } = location.state || {};
  const vehicleType = _vehicleType || data?.vehicleType || data?.vehicle_type || '';

  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_LOADER_ID,
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // ── Refs ────────────────────────────────────────────────────────────────────
  const mapRef = useRef(null);
  const geocoderRef = useRef(null);
  const autocompleteRef = useRef(null);
  const searchTimerRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const animationRef = useRef(null);
  const lastEndTimeRef = useRef(null);
  const noDataTimeoutRef = useRef(null);
  const lastVehiclePositionRef = useRef(null);
  const routeRecalculationTimeoutRef = useRef(null);
  const stoppedSinceRef = useRef(null); // timestamp when speed first became 0

  // ── State ───────────────────────────────────────────────────────────────────
  const [vehicleTrackingData, setVehicleTrackingData] = useState([]);
  const [routeCoordinates, setRouteCoordinates] = useState([]);
  const [routePoints, setRoutePoints] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [keyPointsAddresses, setKeyPointsAddresses] = useState({});
  const [tripStartTime, setTripStartTime] = useState(null);
  const [tripEndTime, setTripEndTime] = useState(null);
  const [showStartModal, setShowStartModal] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);
  const [isApiLoading, setIsApiLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(data?.status);
  const [movingVehiclePos, setMovingVehiclePos] = useState(null);
  const [hasVehiclePosition, setHasVehiclePosition] = useState(false);
  const [allTrackingPoints, setAllTrackingPoints] = useState([]);
  const [combinedRoute, setCombinedRoute] = useState([]);
  const [noDataToastShown, setNoDataToastShown] = useState(false);
  const [activeInfoWindow, setActiveInfoWindow] = useState(null);
  const [vehicleColorStatus, setVehicleColorStatus] = useState('offline');
  const [vehicleHeading, setVehicleHeading] = useState(0);
  const [showVehiclePanel, setShowVehiclePanel] = useState(false);

  const IDLE_THRESHOLD_MS = 15000; // 15s at speed 0 → transition red → yellow
  const GPS_TIMEOUT_MS    = 60000; // 60s no new GPS point → ash

  // ── Layer switcher state (replaces mapTypeId) ──────────────────────────────
  const [selectedLayer, setSelectedLayer] = useState("road");
  const [showLayerControl, setShowLayerControl] = useState(false);
  const [showTransit, setShowTransit] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const [animationIndex, setAnimationIndex] = useState(0);

  // Route segmentation
  const [activeSegmentIndex, setActiveSegmentIndex] = useState(0);
  const [segmentedRoutes, setSegmentedRoutes] = useState([]);
  const [currentSegmentCoordinates, setCurrentSegmentCoordinates] = useState([]);
  const [nextSegmentCoordinates, setNextSegmentCoordinates] = useState([]);
  const [userProximityToPoints, setUserProximityToPoints] = useState({});
  const [completedSegments, setCompletedSegments] = useState([]);
  const [isSegmentDynamic, setIsSegmentDynamic] = useState(false);

  // ── Cleanup ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (noDataTimeoutRef.current) clearTimeout(noDataTimeoutRef.current);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (routeRecalculationTimeoutRef.current) clearTimeout(routeRecalculationTimeoutRef.current);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      toast.dismiss("no-gps-data");
    };
  }, []);

  useEffect(() => { if (data?.status) setCurrentStatus(data.status); }, [data]);

  // ── Haversine distance (km) ─────────────────────────────────────────────────
  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const findNearestPointOnRoute = (cLat, cLng, points) => {
    let nearest = null, minDist = Infinity, nearestIdx = -1;
    points.forEach((p, i) => {
      const d = calculateDistance(cLat, cLng, p.lat, p.lng);
      if (d < minDist) { minDist = d; nearest = p; nearestIdx = i; }
    });
    return { point: nearest, distance: minDist, index: nearestIdx };
  };

  // ── Google Roads API ────────────────────────────────────────────────────────
  const snapToRoadGoogle = useCallback(async (points) => {
    if (!points || points.length < 2) return points?.map((p) => ({ lat: p.lat, lng: p.lng })) || [];
    const CHUNK = 100;
    const all = [];

    for (let start = 0; start < points.length; start += CHUNK - 1) {
      const chunk = points.slice(start, start + CHUNK);
      const pathStr = chunk.map((p) => `${p.lat},${p.lng}`).join("|");
      try {
        const url = `https://roads.googleapis.com/v1/snapToRoads?path=${encodeURIComponent(pathStr)}&interpolate=true&key=${GOOGLE_MAPS_API_KEY}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Roads API ${res.status}`);
        const json = await res.json();

        if (json.snappedPoints?.length > 0) {
          const snapped = json.snappedPoints.map((sp) => ({
            lat: sp.location.latitude,
            lng: sp.location.longitude,
          }));
          all.length > 0 && start > 0 ? all.push(...snapped.slice(1)) : all.push(...snapped);
        } else {
          const raw = chunk.map((p) => ({ lat: p.lat, lng: p.lng }));
          all.length > 0 && start > 0 ? all.push(...raw.slice(1)) : all.push(...raw);
        }
      } catch (err) {
        console.warn("Roads API chunk failed:", err.message);
        const raw = chunk.map((p) => ({ lat: p.lat, lng: p.lng }));
        all.length > 0 && start > 0 ? all.push(...raw.slice(1)) : all.push(...raw);
      }
      if (start + CHUNK < points.length) await new Promise((r) => setTimeout(r, 200));
    }
    return all;
  }, []);

  // ── Google Directions API ───────────────────────────────────────────────────
  const decodePolyline = (encoded) => {
    const pts = [];
    let idx = 0, lat = 0, lng = 0;
    while (idx < encoded.length) {
      let b, shift = 0, result = 0;
      do { b = encoded.charCodeAt(idx++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
      lat += (result & 1) ? ~(result >> 1) : result >> 1;
      shift = 0; result = 0;
      do { b = encoded.charCodeAt(idx++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
      lng += (result & 1) ? ~(result >> 1) : result >> 1;
      pts.push({ lat: lat / 1e5, lng: lng / 1e5 });
    }
    return pts;
  };

  const getDirectionsGoogle = useCallback(async (origin, destination, waypoints = []) => {
    try {
      const wpStr = waypoints.map((w) => `${w.lat},${w.lng}`).join("|");
      const url =
        `https://maps.googleapis.com/maps/api/directions/json` +
        `?origin=${origin.lat},${origin.lng}` +
        `&destination=${destination.lat},${destination.lng}` +
        (wpStr ? `&waypoints=${encodeURIComponent(wpStr)}` : "") +
        `&key=${GOOGLE_MAPS_API_KEY}`;
      const res = await fetch(url);
      const json = await res.json();
      if (json.status === "OK" && json.routes.length > 0) {
        return decodePolyline(json.routes[0].overview_polyline.points);
      }
      throw new Error(json.status);
    } catch (err) {
      console.warn("Directions API failed:", err.message);
      return [origin, destination];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const snapPointsToRoad = useCallback(async (points) => {
    if (!points || points.length < 2) return points?.map((p) => ({ lat: p.lat, lng: p.lng })) || [];
    try {
      const snapped = await snapToRoadGoogle(points);
      if (snapped?.length >= 2) return snapped;
    } catch { /* fall through */ }
    const wps = points.slice(1, -1).filter((_, i, a) =>
      i % Math.max(1, Math.floor(a.length / 8)) === 0
    );
    return getDirectionsGoogle(points[0], points[points.length - 1], wps);
  }, [snapToRoadGoogle, getDirectionsGoogle]);

  // Cache to avoid repeated API calls
  const geocache = new Map();

  const getAddressFromLatLng = useCallback(async (lat, lng) => {
    const cacheKey = `${lat},${lng}`;
    if (geocache.has(cacheKey)) return geocache.get(cacheKey);

    try {
      const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Geocoding failed');
      const data = await response.json();

      if (data.status === 'OK' && data.results.length > 0) {
        const addressComponents = data.results[0].address_components;
        const getComponent = (types) => {
          const component = addressComponents.find(c => types.some(type => c.types.includes(type)));
          return component ? component.long_name : '';
        };

        const sublocality2 = getComponent(['sublocality_level_2']);
        const sublocality1 = getComponent(['sublocality_level_1']);
        const locality = getComponent(['locality']);
        const administrativeArea = getComponent(['administrative_area_level_1']);
        const postalCode = getComponent(['postal_code']);
        const premise = getComponent(['premise', 'point_of_interest', 'establishment']);

        const addressParts = [];
        if (premise) addressParts.push(premise);
        if (sublocality2) addressParts.push(sublocality2);
        if (sublocality1) addressParts.push(sublocality1);
        if (locality) addressParts.push(locality);

        const stateAndPincode = [];
        if (administrativeArea) stateAndPincode.push(administrativeArea);
        if (postalCode) stateAndPincode.push(postalCode);
        if (stateAndPincode.length > 0) addressParts.push(stateAndPincode.join(' '));

        if (addressParts.length > 0) {
          const shortAddress = addressParts.join(', ');
          geocache.set(cacheKey, shortAddress);
          return shortAddress;
        }
        const formattedAddress = data.results[0].formatted_address;
        geocache.set(cacheKey, formattedAddress);
        return formattedAddress;
      } else {
        const fallback = `${parseFloat(lat).toFixed(5)}, ${parseFloat(lng).toFixed(5)}`;
        geocache.set(cacheKey, fallback);
        return data.status === 'ZERO_RESULTS' ? 'Address not found' : fallback;
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      const fallback = `${parseFloat(lat).toFixed(5)}, ${parseFloat(lng).toFixed(5)}`;
      geocache.set(cacheKey, fallback);
      return fallback;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── GPS helpers ─────────────────────────────────────────────────────────────
  const filterGPSPoints = (points, minDist = 0.005) => {
    if (points.length <= 2) return points;
    const out = [points[0]];
    for (let i = 1; i < points.length - 1; i++) {
      const d = calculateDistance(
        parseFloat(out[out.length - 1].lat), parseFloat(out[out.length - 1].lng),
        parseFloat(points[i].lat), parseFloat(points[i].lng)
      );
      if (d >= minDist) out.push(points[i]);
    }
    out.push(points[points.length - 1]);
    return out;
  };
  console.log("filter", filterGPSPoints);

  const validateAndFilterGPSPoints = (points, opts = {}) => {
    const cfg = { maxSpeed: 150, maxDistanceJump: 2, ...opts };
    if (points.length <= 2) return points;
    const out = [points[0]];
    for (let i = 1; i < points.length - 1; i++) {
      const cur = points[i], prev = out[out.length - 1];
      const d = calculateDistance(
        parseFloat(prev.latitude), parseFloat(prev.longitude),
        parseFloat(cur.latitude), parseFloat(cur.longitude)
      );
      const t = Math.abs(
        new Date(cur.vehicle_tracking_time) - new Date(prev.vehicle_tracking_time)
      ) / 3600000;
      if (t < 0.001) continue;
      if (d <= cfg.maxDistanceJump && d / t <= cfg.maxSpeed) out.push(cur);
      else console.warn(`Filtered GPS outlier: ${d.toFixed(3)}km at ${(d / t).toFixed(0)}km/h`);
    }
    if (points.length > 1) out.push(points[points.length - 1]);
    return out;
  };

  // ── Format helpers ──────────────────────────────────────────────────────────
  const pad = (n) => String(n).padStart(2, "0");
  const formatDateTime = (date) =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;

  const formatTrackingTime = (ts) => {
    const d = new Date(ts);
    let h = d.getHours();
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ` +
      `${pad(h)}:${pad(d.getMinutes())} ${ampm}`;
  };

  const getSpeedColor = (spd) =>
    spd <= 40 ? "speed-safe" : spd <= 60 ? "speed-warning" : "speed-danger";

  const getHaltPoints = () => routePoints.filter((p) => p.pointType === "halt");

  const getMapCenter = () => {
    if (routeCoordinates.length > 0) return routeCoordinates[0];
    if (vehicleTrackingData.length > 0)
      return { lat: parseFloat(vehicleTrackingData[0].latitude), lng: parseFloat(vehicleTrackingData[0].longitude) };
    return DEFAULT_CENTER;
  };

  const geocodeKeyPointsOnly = useCallback(async (kps) => {
    const results = await Promise.allSettled(
      kps.map(async (p, i) => {
        await new Promise((r) => setTimeout(r, i * 250));
        const addr = await getAddressFromLatLng(parseFloat(p.latitude), parseFloat(p.longitude));
        return { id: p.id, addr };
      })
    );
    const map = {};
    results.forEach((r) => {
      if (r.status === "fulfilled") map[r.value.id] = r.value.addr;
      else console.warn("Geocoding failed for a point:", r.reason);
    });
    console.log("Geocoded addresses:", map);
    setKeyPointsAddresses(map);
  }, [getAddressFromLatLng]);

  // ── Fetch user profile ──────────────────────────────────────────────────────
  const fetchUserProfile = useCallback(async () => {
    try {
      const res = await getData(getThemeURL);
      if (Array.isArray(res) && res.length > 0) setUserId(res[0].id);
    } catch (e) { console.error("fetchUserProfile:", e); }
  }, []);

  useEffect(() => { fetchUserProfile(); }, [fetchUserProfile]);

  // ── Load route / waypoint data ──────────────────────────────────────────────
  const vehicleData = useCallback(async (ID) => {
    setIsLoadingData(true);
    try {
      const resp = await getData(getAssignedWaypoints.replace("ID", ID));
      setVehicleTrackingData(resp);

      if (resp.length > 1) {
        const validTypes = ["start", "halt", "end"];
        const allValid = resp.every((p) => validTypes.includes(p.pointType?.toLowerCase()));
        const pts = resp.map((p) => ({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) }));

        const snapped = allValid ? await snapPointsToRoad(pts) : pts;
        setRouteCoordinates(snapped);

        const kps = resp.filter((p) => validTypes.includes(p.pointType?.toLowerCase()));
        setRoutePoints(kps);
        if (kps.length > 0) geocodeKeyPointsOnly(kps);
      } else {
        setRouteCoordinates([]);
        setRoutePoints([]);
      }
    } catch (err) {
      console.error("vehicleData:", err);
      toast.error("Failed to load vehicle data");
    } finally {
      setIsLoadingData(false);
    }
  }, [snapPointsToRoad, geocodeKeyPointsOnly]);

  // ── Route segmentation ──────────────────────────────────────────────────────
  const segmentRoute = useCallback(() => {
    if (routePoints.length < 2) return;
    const allCoords = vehicleTrackingData.map((p) => ({
      lat: parseFloat(p.latitude), lng: parseFloat(p.longitude),
      id: p.id, pointType: p.pointType,
    }));

    const segments = [];
    for (let i = 0; i < routePoints.length - 1; i++) {
      const sp = routePoints[i], ep = routePoints[i + 1];
      const si = allCoords.findIndex(
        (c) => Math.abs(c.lat - parseFloat(sp.latitude)) < 0.0001 &&
          Math.abs(c.lng - parseFloat(sp.longitude)) < 0.0001
      );
      const ei = allCoords.findIndex(
        (c) => Math.abs(c.lat - parseFloat(ep.latitude)) < 0.0001 &&
          Math.abs(c.lng - parseFloat(ep.longitude)) < 0.0001
      );

      const coords =
        si !== -1 && ei !== -1 && si < ei
          ? allCoords.slice(si, ei + 1)
          : [
            { lat: parseFloat(sp.latitude), lng: parseFloat(sp.longitude), id: sp.id, pointType: sp.pointType },
            { lat: parseFloat(ep.latitude), lng: parseFloat(ep.longitude), id: ep.id, pointType: ep.pointType },
          ];

      segments.push({
        id: `seg-${i}`, startPoint: sp, endPoint: ep,
        coordinates: coords, plannedCoordinates: coords, isCompleted: false,
      });
    }

    setSegmentedRoutes(segments);
    if (segments.length > 0) {
      setCurrentSegmentCoordinates(segments[0].coordinates);
      if (segments.length > 1) setNextSegmentCoordinates(segments[1].coordinates);
    }
    setIsSegmentDynamic(false);
  }, [routePoints, vehicleTrackingData]);

  useEffect(() => {
    if (routePoints.length > 0 && vehicleTrackingData.length > 0) segmentRoute();
  }, [routePoints, vehicleTrackingData, segmentRoute]);

  // ── Proximity & segment advancement ────────────────────────────────────────
  const markSegmentAsCompleted = useCallback((idx) => {
    setSegmentedRoutes((prev) => prev.map((s, i) => (i === idx ? { ...s, isCompleted: true } : s)));
    setCompletedSegments((prev) => [...prev, idx]);
  }, []);

  const advanceToNextSegment = useCallback(() => {
    if (activeSegmentIndex >= segmentedRoutes.length - 1) return;
    markSegmentAsCompleted(activeSegmentIndex);
    const next = activeSegmentIndex + 1;
    setActiveSegmentIndex(next);
    setCurrentSegmentCoordinates(segmentedRoutes[next].coordinates);
    setIsSegmentDynamic(false);
    if (next + 1 < segmentedRoutes.length) setNextSegmentCoordinates(segmentedRoutes[next + 1].coordinates);
    else setNextSegmentCoordinates([]);
    toast.info("Progressing to next waypoint", { autoClose: 3000 });
  }, [activeSegmentIndex, segmentedRoutes, markSegmentAsCompleted]);

  const checkUserProximity = useCallback(() => {
    if (!movingVehiclePos || !segmentedRoutes.length) return;
    const { lat: cLat, lng: cLng } = movingVehiclePos;
    const THRESHOLD = 0.05;

    const seg = segmentedRoutes[activeSegmentIndex];
    if (seg && !seg.isCompleted) {
      const d = calculateDistance(cLat, cLng, parseFloat(seg.endPoint.latitude), parseFloat(seg.endPoint.longitude));
      setUserProximityToPoints((prev) => ({ ...prev, [seg.endPoint.id]: d }));
      if (d <= THRESHOLD) advanceToNextSegment();
    }

    segmentedRoutes.forEach((s, idx) => {
      if (idx < activeSegmentIndex && !s.isCompleted) {
        const d = calculateDistance(cLat, cLng, parseFloat(s.endPoint.latitude), parseFloat(s.endPoint.longitude));
        if (d <= THRESHOLD) markSegmentAsCompleted(idx);
      }
    });
  }, [movingVehiclePos, segmentedRoutes, activeSegmentIndex, advanceToNextSegment, markSegmentAsCompleted]);

  // ── Route deviation & dynamic recalculation ─────────────────────────────────
  const checkForRouteDeviation = useCallback(() => {
    if (!movingVehiclePos || currentSegmentCoordinates.length < 2) return;
    const { lat: cLat, lng: cLng } = movingVehiclePos;
    const { distance } = findNearestPointOnRoute(cLat, cLng, currentSegmentCoordinates);
    const threshold = isSegmentDynamic ? 0.3 : 0.15;

    const seg = segmentedRoutes[activeSegmentIndex];
    if (seg) {
      const dEnd = calculateDistance(cLat, cLng, parseFloat(seg.endPoint.latitude), parseFloat(seg.endPoint.longitude));
      if (dEnd < 0.05) return;
    }

    if (distance > threshold && !isSegmentDynamic) recalculateDynamicRoute();
    lastVehiclePositionRef.current = { lat: cLat, lng: cLng };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movingVehiclePos, currentSegmentCoordinates, isSegmentDynamic, segmentedRoutes, activeSegmentIndex]);

  const recalculateDynamicRoute = useCallback(async () => {
    if (!movingVehiclePos || !segmentedRoutes[activeSegmentIndex]) return;
    const { lat: cLat, lng: cLng } = movingVehiclePos;
    const seg = segmentedRoutes[activeSegmentIndex];

    try {
      const original = seg.plannedCoordinates || seg.coordinates;
      let closestIdx = 0, minD = Infinity;
      original.forEach((p, i) => {
        const d = calculateDistance(cLat, cLng, p.lat, p.lng);
        if (d < minD) { minD = d; closestIdx = i; }
      });

      const remaining = original.slice(Math.max(0, closestIdx - 1));
      const route = await snapPointsToRoad([{ lat: cLat, lng: cLng }, ...remaining]);

      if (route?.length > 1) {
        const fmt = route.map((p, i) => ({ ...p, id: `dyn-${Date.now()}-${i}`, pointType: "dynamic" }));
        setCurrentSegmentCoordinates(fmt);
        setIsSegmentDynamic(true);
        toast.info("Route updated based on current position", { autoClose: 3000 });
        setSegmentedRoutes((prev) =>
          prev.map((s, i) => (i === activeSegmentIndex ? { ...s, coordinates: fmt } : s))
        );
      }
    } catch (err) {
      console.error("Dynamic route recalc failed:", err);
      const ep = seg.endPoint;
      const simple = await snapPointsToRoad([
        { lat: cLat, lng: cLng },
        { lat: parseFloat(ep.latitude), lng: parseFloat(ep.longitude) },
      ]);
      if (simple?.length > 1) {
        const fmt = simple.map((p, i) => ({ ...p, id: `dyn-fb-${Date.now()}-${i}`, pointType: "dynamic" }));
        setCurrentSegmentCoordinates(fmt);
        setIsSegmentDynamic(true);
      }
    }
  }, [movingVehiclePos, segmentedRoutes, activeSegmentIndex, snapPointsToRoad]);

  useEffect(() => {
    if (currentStatus === "IN PROGRESS" && movingVehiclePos) {
      checkUserProximity();
      checkForRouteDeviation();
    }
  }, [movingVehiclePos, currentStatus, checkUserProximity, checkForRouteDeviation]);

  // ── Combined live route ─────────────────────────────────────────────────────
  const createCombinedRoute = useCallback(async () => {
    if (!vehicleTrackingData.length || !allTrackingPoints.length) return;
    const start = vehicleTrackingData[0];
    const pts = [
      { lat: parseFloat(start.latitude), lng: parseFloat(start.longitude) },
      ...allTrackingPoints.map((p) => ({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) })),
    ];
    if (pts.length < 2) return;
    const snapped = await snapPointsToRoad(pts);
    setCombinedRoute(snapped?.length >= 2 ? snapped : pts);
  }, [vehicleTrackingData, allTrackingPoints, snapPointsToRoad]);

  useEffect(() => {
    if (currentStatus === "IN PROGRESS" && allTrackingPoints.length > 0 && vehicleTrackingData.length > 0)
      createCombinedRoute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTrackingPoints.length, currentStatus]);

  // ── Vehicle color status based on speed + GPS freshness ────────────────────
  useEffect(() => {
    if (!allTrackingPoints.length) {
      setVehicleColorStatus('offline');
      return;
    }
    const last  = allTrackingPoints[allTrackingPoints.length - 1];
    const speed = parseFloat(last?.speed ?? 0);
    const lastTs = last?.vehicle_tracking_time
      ? new Date(last.vehicle_tracking_time.replace(' ', 'T')).getTime()
      : null;

    // No recent GPS → ash
    if (!lastTs || (Date.now() - lastTs) > GPS_TIMEOUT_MS) {
      stoppedSinceRef.current = null;
      setVehicleColorStatus('offline');
      return;
    }

    if (speed > 2) {
      stoppedSinceRef.current = null;
      setVehicleColorStatus('moving');
    } else {
      if (stoppedSinceRef.current === null) {
        stoppedSinceRef.current = Date.now();
        setVehicleColorStatus('just-stopped');
      } else if (Date.now() - stoppedSinceRef.current >= IDLE_THRESHOLD_MS) {
        setVehicleColorStatus('idle');
      } else {
        setVehicleColorStatus('just-stopped');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTrackingPoints]);

  // Transition red → yellow after IDLE_THRESHOLD_MS when vehicle stays stopped
  useEffect(() => {
    if (vehicleColorStatus !== 'just-stopped') return;
    const elapsed   = stoppedSinceRef.current ? Date.now() - stoppedSinceRef.current : IDLE_THRESHOLD_MS;
    const remaining = Math.max(0, IDLE_THRESHOLD_MS - elapsed);
    const timer = setTimeout(() => {
      setVehicleColorStatus(prev => prev === 'just-stopped' ? 'idle' : prev);
    }, remaining);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleColorStatus]);

  // ── Animation (requestAnimationFrame — smooth interpolation between road points) ──
  const startTrackingAnimation = useCallback((coords, startIdx = 0) => {
    if (!coords?.length) return;
    if (animationRef.current) cancelAnimationFrame(animationRef.current);

    let segIdx  = startIdx;
    let segStart = null;
    const SEG_MS = 500; // ms to travel between consecutive snapped road points

    setMovingVehiclePos(coords[segIdx]);
    setAnimationIndex(segIdx);

    const tick = (ts) => {
      if (segIdx + 1 >= coords.length) return;
      if (segStart === null) segStart = ts;

      const t    = Math.min((ts - segStart) / SEG_MS, 1);
      const from = coords[segIdx];
      const to   = coords[segIdx + 1];

      setMovingVehiclePos({
        lat: from.lat + (to.lat - from.lat) * t,
        lng: from.lng + (to.lng - from.lng) * t,
      });

      if (t >= 1) {
        setVehicleHeading(calcBearing(from, to));
        segIdx++;
        setAnimationIndex(segIdx);
        segStart = null;
      }

      if (segIdx + 1 < coords.length) {
        animationRef.current = requestAnimationFrame(tick);
      }
    };

    animationRef.current = requestAnimationFrame(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Polling ─────────────────────────────────────────────────────────────────
  const vehicleData1 = useCallback(async (vehicleno, startTime, endTime) => {
    try {
      const newData = await postDataWithBody(fetchVehicleTracking, { vehicleNumber: vehicleno, startTime, endTime }, {});

      if (noDataTimeoutRef.current) { clearTimeout(noDataTimeoutRef.current); noDataTimeoutRef.current = null; }
      if (newData.length > 0 && noDataToastShown) { toast.dismiss("no-gps-data"); setNoDataToastShown(false); }

      if (!newData?.length) {
        lastEndTimeRef.current = endTime;
        setNoDataToastShown(false);
        setTimeout(() => {
          toast.warning("No vehicle GPS points available yet", {
            autoClose: 1000, toastId: "no-gps-data", position: "bottom-right",
          });
          setNoDataToastShown(true);
        }, 15000);
        return [];
      }

      toast.dismiss("no-gps-data");
      lastEndTimeRef.current = endTime;

      const validated = validateAndFilterGPSPoints(newData, { maxSpeed: 120, maxDistanceJump: 2 });
      setAllTrackingPoints((prev) => [...prev, ...validated]);

      if (validated.length > 0) {
        const coords = validated.map((p) => ({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) }));
        setHasVehiclePosition(true);

        if (coords.length >= 2) {
          const snapped = await snapPointsToRoad(coords);
          if (snapped?.length >= 2) startTrackingAnimation(snapped);
        } else {
          setMovingVehiclePos(coords[0]);
        }
        setNoDataToastShown(false);
      } else {
        setHasVehiclePosition(true);
      }
    } catch (err) {
      console.error("vehicleData1:", err);
      lastEndTimeRef.current = endTime;
      if (noDataTimeoutRef.current) { clearTimeout(noDataTimeoutRef.current); noDataTimeoutRef.current = null; }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapPointsToRoad, startTrackingAnimation, noDataToastShown]);

  const startPolling = useCallback((vehicleNumber) => {
    setNoDataToastShown(false);
    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);

    const now = formatDateTime(new Date());
    const start = lastEndTimeRef.current || now;
    vehicleData1(vehicleNumber, start, now);

    pollingIntervalRef.current = setInterval(() => {
      const newEnd = formatDateTime(new Date());
      vehicleData1(vehicleNumber, lastEndTimeRef.current, newEnd);
    }, 5000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleData1]);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) { clearInterval(pollingIntervalRef.current); pollingIntervalRef.current = null; }
    if (noDataTimeoutRef.current) { clearTimeout(noDataTimeoutRef.current); noDataTimeoutRef.current = null; }
    toast.dismiss("no-gps-data");
    lastEndTimeRef.current = null;
    setHasVehiclePosition(true);
    setAllTrackingPoints([]);
    setCombinedRoute([]);
    setNoDataToastShown(false);
    toast.info("Live tracking stopped", { autoClose: 2000 });
  }, []);

  const resumeTripTracking = useCallback(async (vehicleNumber) => {
    try {
      const now = new Date();
      const five = new Date(now.getTime() - 5 * 60 * 1000);
      const recentStart = formatDateTime(five);
      const current = formatDateTime(now);

      const recent = await postDataWithBody(fetchVehicleTracking, { vehicleNumber, startTime: recentStart, endTime: current }, {});

      if (recent?.length > 0) {
        const valid = validateAndFilterGPSPoints(recent, { maxSpeed: 120, maxDistanceJump: 2 });
        setAllTrackingPoints(valid);
        setHasVehiclePosition(valid.length > 0);

        if (valid.length > 0) {
          const coords = valid.map((p) => ({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) }));
          if (coords.length >= 2) {
            const snapped = await snapPointsToRoad(coords);
            if (snapped?.length >= 2) startTrackingAnimation(snapped, Math.max(0, snapped.length - 3));
          } else setMovingVehiclePos(coords[0]);
        }
        lastEndTimeRef.current = valid[valid.length - 1]?.vehicle_tracking_time || recentStart;
      } else {
        setHasVehiclePosition(false);
        lastEndTimeRef.current = recentStart;
      }
      startPolling(vehicleNumber);
    } catch (err) {
      console.error("resumeTripTracking:", err);
      lastEndTimeRef.current = formatDateTime(new Date());
      setHasVehiclePosition(false);
      startPolling(vehicleNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapPointsToRoad, startTrackingAnimation, startPolling]);

  // ── Initial data load ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!data) return;
    vehicleData(data.id);
    if (data.status === "IN PROGRESS" && data?.actualStartDate && !data?.actualEndDate) {
      setTripStartTime(formatDateTime(new Date(data.actualStartDate)));
      resumeTripTracking(data.vehicleNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // ── Trip start / end ────────────────────────────────────────────────────────
  const confirmStartTrip = async () => {
    setIsApiLoading(true);
    try {
      const now = formatDateTime(new Date());
      const payload = {
        vehicleNumber: data?.vehicleNumber || "TN09AB1234",
        routeId: data?.routeId || 102,
        assignedRouteId: data?.id,
        routeName: data?.routeName || "Route-101",
        deviceId: data?.deviceId || "",
        createdBy: userId,
        startDate: now,
        endDate: null,
      };
      const resp = await postDataWithBody(travelstarttripapi, payload, {});
      if (resp.status === "success") {
        setTripStartTime(now);
        lastEndTimeRef.current = now;
        setCurrentStatus("IN PROGRESS");
        setShowStartModal(false);
        toast.success(resp.message, { position: "top-center", autoClose: 3000 });
        startPolling(data?.vehicleNumber);
      } else {
        toast.error(resp.message, { position: "top-center", autoClose: 3000 });
      }
    } catch { toast.error("Failed to start trip. Please try again."); }
    finally { setIsApiLoading(false); }
  };

  const confirmEndTrip = async () => {
    setIsApiLoading(true);
    try {
      const now = formatDateTime(new Date());
      const resp = await putDataWithBody(
        travelendtripapi
          .replace("VNO", data?.vehicleNumber)
          .replace("RID", data?.routeId)
          .replace("ENDDate", encodeURIComponent(now))
          .replace("assignid", data?.id),
        "", {}
      );
      if (resp.status === "success") {
        setTripEndTime(now);
        setCurrentStatus("TRAVELED");
        setShowEndModal(false);
        stopPolling();
        toast.success(resp.message, { position: "top-center", autoClose: 3000 });
      } else {
        toast.error(resp.message, { position: "top-center", autoClose: 3000 });
      }
    } catch { toast.error("Failed to end trip. Please try again."); }
    finally { setIsApiLoading(false); }
  };

  // ── Search helpers ─────────────────────────────────────────────────────────
  const searchLocation = useCallback((query) => {
    if (!query.trim()) { setSearchResults([]); return; }
    if (!autocompleteRef.current) return;
    setIsSearching(true);
    autocompleteRef.current.getPlacePredictions(
      { input: query, componentRestrictions: { country: "in" }, types: ["geocode", "establishment"] },
      (predictions, status) => {
        setIsSearching(false);
        const OK = window.google.maps.places.PlacesServiceStatus.OK;
        if (status === OK && predictions?.length) {
          setSearchResults(predictions.map((p) => ({ id: p.place_id, name: p.description, placeId: p.place_id })));
        } else { setSearchResults([]); }
      }
    );
  }, []);

  const selectSearchResult = useCallback((result) => {
    setSearchQuery(result.name);
    setSearchResults([]);
    if (!geocoderRef.current) return;
    geocoderRef.current.geocode({ placeId: result.placeId }, (results, status) => {
      if (status === "OK" && results[0]) {
        const loc = results[0].geometry.location;
        if (mapRef.current) {
          if (results[0].geometry.viewport) mapRef.current.fitBounds(results[0].geometry.viewport);
          else { mapRef.current.setCenter({ lat: loc.lat(), lng: loc.lng() }); mapRef.current.setZoom(15); }
        }
        toast.success(`Map centred on: ${result.name.split(",")[0]}`);
      }
    });
  }, []);

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => searchLocation(query), 500);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
  };

  // ── Map callbacks ───────────────────────────────────────────────────────────
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    geocoderRef.current = new window.google.maps.Geocoder();
    autocompleteRef.current = new window.google.maps.places.AutocompleteService();
  }, []);

  const fitMapBounds = useCallback(() => {
    const map = mapRef.current;
    if (!map || !isLoaded) return;

    const bounds = new window.google.maps.LatLngBounds();
    let hasPoints = false;

    const add = (lat, lng) => { bounds.extend({ lat, lng }); hasPoints = true; };

    if (currentSegmentCoordinates.length > 0) {
      currentSegmentCoordinates.forEach((p) => add(p.lat, p.lng));
    } else {
      vehicleTrackingData.forEach((p) => add(parseFloat(p.latitude), parseFloat(p.longitude)));
      routeCoordinates.forEach((p) => add(p.lat, p.lng));
      routePoints.forEach((p) => add(parseFloat(p.latitude), parseFloat(p.longitude)));
    }

    if (hasPoints) {
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
      const listener = window.google.maps.event.addListenerOnce(map, "bounds_changed", () => {
        if (map.getZoom() > 16) map.setZoom(16);
      });
      return () => window.google.maps.event.removeListener(listener);
    }
  }, [isLoaded, vehicleTrackingData, routeCoordinates, routePoints, currentSegmentCoordinates]);

  useEffect(() => { fitMapBounds(); }, [fitMapBounds]);

  // ── Early returns (AFTER all hooks) ────────────────────────────────────────
  if (loadError) return <div className="p-4 text-danger">Google Maps failed to load: {loadError.message}</div>;
  if (!isLoaded) return (
    <div className="p-4 d-flex align-items-center gap-2 text-muted">
      <span className="spinner-border spinner-border-sm" role="status" />
      Loading Google Maps…
    </div>
  );

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="trip-container-modern">
      {/* Modals */}
      <ConfirmationModal isOpen={showStartModal} onClose={() => setShowStartModal(false)}
        onConfirm={confirmStartTrip} title="Start Trip"
        message={`Start trip for vehicle ${data?.vehicleNumber}?`}
        confirmText="Start Trip" type="start" isLoading={isApiLoading} />
      <ConfirmationModal isOpen={showEndModal} onClose={() => setShowEndModal(false)}
        onConfirm={confirmEndTrip} title="End Trip"
        message={`End trip for vehicle ${data?.vehicleNumber}?`}
        confirmText="End Trip" type="end" isLoading={isApiLoading} />

      <div className="row" style={{ margin: 0, alignItems: "stretch" }}>
        {/* ── Left Panel ── */}
        <div className="col-lg-4" style={{ display: "flex", flexDirection: "column", padding: "14px 8px 14px 10px" }}>
      {/* ── Header ── */}
      <div className="trip-header-modern" style={{ flex: 1, borderRadius: 12, borderBottom: "none", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
        {/* Section label */}
        <div className="trip-section-label">
          <div className="trip-section-label-bar"></div>
          <span className="trip-section-label-text">Trip Details</span>
        </div>
        {/* Info cards */}
        <div className="trip-info-grid" style={{ flexDirection: "column", gap: 8 }}>
          <div className="info-card-modern vehicle-card">
            <div className="info-icon-modern">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2">
                <path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2"/>
                <circle cx="6.5" cy="16.5" r="2.5"/>
                <circle cx="16.5" cy="16.5" r="2.5"/>
              </svg>
            </div>
            <div className="info-content-modern">
              <div className="info-label-modern">Vehicle Number</div>
              <div className="info-value-modern">{data?.vehicleNumber || "N/A"}</div>
            </div>
          </div>

          <div className="info-card-modern route-name-card" style={{ flex: "none" }}>
            <div className="info-icon-modern">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2">
                <path d="M3 11l7-7 7 7M10 21v-8h4v8"/>
                <path d="M3 17h18"/>
              </svg>
            </div>
            <div className="info-content-modern">
              <div className="info-label-modern">Route Name</div>
              <div className="info-value-modern" title={data?.routeName} style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{data?.routeName || "N/A"}</div>
            </div>
          </div>

          <div className="info-card-modern start-card">
            <div className="info-icon-modern">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
                <polyline points="9 14 11 16 15 12"/>
              </svg>
            </div>
            <div className="info-content-modern">
              <div className="info-label-modern">Planned Start</div>
              <div className="info-value-modern">{data ? formatTrackingTime(data.startDate) : "—"}</div>
            </div>
          </div>

          <div className="info-card-modern end-card">
            <div className="info-icon-modern">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
                <line x1="12" y1="14" x2="12" y2="18"/>
                <line x1="10" y1="16" x2="14" y2="16"/>
              </svg>
            </div>
            <div className="info-content-modern">
              <div className="info-label-modern">Planned End</div>
              <div className="info-value-modern">{data ? formatTrackingTime(data.endDate) : "—"}</div>
            </div>
          </div>
        </div>

        {/* Combined status + action row */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
          {/* Trip status pills */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {(tripStartTime || data?.actualStartDate) && (
              <div className="timeline-card started-card" style={{ minWidth: "auto", flex: 1 }}>
                <div className="timeline-icon-modern">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <div className="timeline-content-modern">
                  <div className="timeline-label-modern">Trip Started</div>
                  <div className="timeline-time-modern">{formatTrackingTime(tripStartTime || data?.actualStartDate)}</div>
                </div>
              </div>
            )}
            {(tripEndTime || data?.actualEndDate) && (
              <div className="timeline-card completed-card" style={{ minWidth: "auto", flex: 1 }}>
                <div className="timeline-icon-modern">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <div className="timeline-content-modern">
                  <div className="timeline-label-modern">Trip Completed</div>
                  <div className="timeline-time-modern">{formatTrackingTime(tripEndTime || data?.actualEndDate)}</div>
                </div>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div style={{ display: "flex", gap: 8 }}>
            {(currentStatus === "NOT YET TRAVELED" || currentStatus === "NOT TRAVELED") &&
              !tripStartTime && !data?.actualStartDate && (
                <button onClick={() => setShowStartModal(true)} className="btn-trip-action btn-start-modern"
                  style={{ flex: 1, padding: "10px 16px", fontSize: 13 }}>
                  <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
                    <path d="M6 4L14 10L6 16V4Z" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Start Trip
                </button>
              )}

            {(currentStatus === "IN PROGRESS" || currentStatus === "GPS_NOT_AVAILABLE") && !tripEndTime && (
              <button onClick={() => setShowEndModal(true)} className="btn-trip-action btn-end-modern"
                style={{ flex: 1, padding: "10px 16px", fontSize: 13 }}>
                <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
                  <rect x="4" y="4" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="2" />
                </svg>
                End Trip
              </button>
            )}

            {(currentStatus === "TRAVELED" || tripEndTime) && (
              <div className="trip-completed-badge" style={{ flex: 1, justifyContent: "center" }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                Trip Completed
              </div>
            )}
          </div>
        </div>
      </div>

        {/* ── Vehicle & Driver Details Panel ── */}
        {showVehiclePanel && vehicleInfo && (
          <div style={{ marginTop: 10, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg,#0f4a45,#0f3d3a)', padding: '8px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2"/>
                  <circle cx="6.5" cy="16.5" r="2.5"/><circle cx="16.5" cy="16.5" r="2.5"/>
                </svg>
                <span style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>Vehicle & Driver Info</span>
              </div>
              <button onClick={() => setShowVehiclePanel(false)}
                style={{ background: 'rgba(255,255,255,0.18)', border: 'none', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', color: '#fff', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>✕</button>
            </div>

            <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>

              {/* Vehicle Details */}
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#0f4a45', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 7, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="#0f4a45"><circle cx="12" cy="12" r="10"/></svg>
                  Vehicle Details
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px' }}>
                  {[
                    { label: 'Type',         value: vehicleInfo.vehicleType },
                    { label: 'Brand',        value: vehicleInfo.brand },
                    { label: 'Model',        value: vehicleInfo.model },
                    { label: 'Fuel Type',    value: vehicleInfo.fuelType },
                    { label: 'Reg. Number', value: vehicleInfo.registrationNumber },
                  ].filter(f => f.value).map(({ label, value }) => (
                    <div key={label} style={{ background: '#f8fafc', borderRadius: 7, padding: '5px 8px' }}>
                      <div style={{ fontSize: 9, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 2 }}>{label}</div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div style={{ borderTop: '1px dashed #e2e8f0' }} />

              {/* Driver Details */}
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#059669', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 7, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="#059669"><circle cx="12" cy="12" r="10"/></svg>
                  Driver Details
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px' }}>
                  {[
                    { label: 'Driver Name',    value: vehicleInfo.driverName },
                    { label: 'Phone',          value: vehicleInfo.driverPhone },
                    { label: 'License No.',    value: vehicleInfo.driverLicense },
                    { label: 'License Expiry', value: vehicleInfo.licenseExpiry },
                  ].filter(f => f.value).map(({ label, value }) => (
                    <div key={label} style={{ background: '#f0fdf4', borderRadius: 7, padding: '5px 8px' }}>
                      <div style={{ fontSize: 9, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 2 }}>{label}</div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{value}</div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        )}
        </div>{/* end left col */}

        {/* ── Right Panel ── */}
        <div className="col-lg-8" style={{ display: "flex", flexDirection: "column", padding: "14px 10px 14px 8px" }}>
          <div style={{ flex: 1, borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,0.09)", display: "flex", flexDirection: "column" }}>
            {/* Gradient Map Card Header */}
            <div style={{ background: "linear-gradient(to right, #0f3d3a 0%, #0f4a45 50%, #1a7a73 100%)", padding: "5px 14px", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <div style={{ width: 26, height: 26, borderRadius: 6, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/>
                  <line x1="9" y1="3" x2="9" y2="18"/>
                  <line x1="15" y1="6" x2="15" y2="21"/>
                </svg>
              </div>
              <span style={{ color: "#fff", fontWeight: 700, fontSize: 13, flex: 1 }}>Route Map</span>
              <span style={{ background: "rgba(255,255,255,0.18)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 20, padding: "2px 9px", fontSize: 10, fontWeight: 600, flexShrink: 0 }}>
                {(LAYERS.find(l => l.id === selectedLayer)?.label ?? selectedLayer)} layer
              </span>
            </div>
            {/* Search bar */}
            <div style={{ padding: "10px 14px 0", position: "relative" }}>
              <div style={{ display: "flex", alignItems: "center", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 9, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                <div style={{ padding: "0 10px", color: "#94a3b8", display: "flex", alignItems: "center" }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                </div>
                <input type="text" placeholder="Search city, area, landmark or coordinates…"
                  value={searchQuery} onChange={handleSearchInputChange}
                  style={{ flex: 1, border: "none", outline: "none", fontSize: 13, padding: "8px 4px", color: "#1e293b", background: "transparent" }} />
                {searchQuery && (
                  <button type="button" onClick={clearSearch}
                    style={{ padding: "0 10px", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 16, display: "flex", alignItems: "center" }}>✕</button>
                )}
                {isSearching && (
                  <div style={{ padding: "0 10px" }}>
                    <div className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: 14, height: 14, borderWidth: 2 }} />
                  </div>
                )}
              </div>
              {searchResults.length > 0 && (
                <div style={{ position: "absolute", left: 14, right: 14, top: "calc(100% - 2px)", zIndex: 1050, background: "#fff", borderRadius: "0 0 10px 10px", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", border: "1px solid #e2e8f0", borderTop: "none", overflow: "hidden" }}>
                  {searchResults.map((result) => (
                    <button key={result.id} type="button" onClick={() => selectSearchResult(result)}
                      className="search-result-row"
                      style={{ width: "100%", padding: "10px 14px", background: "none", border: "none", borderBottom: "1px solid #f1f5f9", cursor: "pointer", textAlign: "left", display: "flex", alignItems: "flex-start", gap: 10 }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 2, flexShrink: 0 }}><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{result.name.split(",")[0]}</div>
                        <div style={{ fontSize: 11, color: "#64748b" }}>{result.name.split(",").slice(1, 3).join(",")}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
      {/* ── Map Wrapper ── */}
      <div className="map-wrapper-modern" style={{ position: "relative", flex: 1, margin: 0, borderRadius: 0, height: "auto", minHeight: 520, boxShadow: "none" }}>

        {/* Loading overlay */}
        {isLoadingData && (
          <div className="map-loading-modern">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
            <div className="loading-text-modern">Loading GPS data…</div>
          </div>
        )}

        {currentStatus === "IN PROGRESS" && !hasVehiclePosition && noDataToastShown && (
          <div className="map-loading-modern">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Waiting…</span>
            </div>
            <div className="loading-text-modern">Waiting for vehicle GPS data…</div>
          </div>
        )}

        {/* ── Map Controls Column ── */}
        <div style={{ position: "absolute", top: 75, right: 20, zIndex: 1000, display: "flex", flexDirection: "column", gap: 8 }}>
          {/* Layer Switcher */}
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setShowLayerControl((p) => !p)}
              style={{
                width: 40, height: 40, borderRadius: 10,
                background: "#fff", border: "1px solid #e2e8f0",
                boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                <polyline points="2 17 12 22 22 17"/>
                <polyline points="2 12 12 17 22 12"/>
              </svg>
            </button>

            {showLayerControl && (
              <div style={{
                position: "absolute", top: 46, right: 0,
                background: "#fff", borderRadius: 10, padding: 8,
                boxShadow: "0 4px 16px rgba(0,0,0,0.14)", minWidth: 150,
                border: "1px solid #e2e8f0",
              }}>
                {LAYERS.map((l) => (
                  <div
                    key={l.id}
                    onClick={() => {
                      setSelectedLayer(l.id);
                      mapRef.current?.setMapTypeId(mapTypes[l.id]);
                      setShowLayerControl(false);
                    }}
                    style={{
                      display: "flex", alignItems: "center", gap: 9,
                      padding: "8px 10px", borderRadius: 7, cursor: "pointer",
                      background: selectedLayer === l.id ? "#f0fdfb" : "transparent",
                      transition: "background 0.15s",
                    }}
                  >
                    <span style={{ color: selectedLayer === l.id ? "#1a7a73" : "#94a3b8" }}>
                      {l.icon}
                    </span>
                    <span style={{
                      fontSize: 13,
                      fontWeight: selectedLayer === l.id ? 600 : 400,
                      color: selectedLayer === l.id ? "#1a7a73" : "#334155",
                    }}>
                      {l.label}
                    </span>
                    {selectedLayer === l.id && (
                      <span style={{
                        marginLeft: "auto", width: 7, height: 7,
                        borderRadius: "50%", background: "#1a7a73",
                      }} />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Transit Layer Toggle */}
          <button
            onClick={() => setShowTransit((p) => !p)}
            title={showTransit ? "Hide transit" : "Show transit"}
            style={{ width: 40, height: 40, borderRadius: 10, background: showTransit ? "#1a7a73" : "#fff", border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={showTransit ? "#fff" : "#555"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="4" y="2" width="16" height="16" rx="2"/>
              <path d="M4 10h16"/><path d="M8 2v8"/><path d="M16 2v8"/>
              <path d="M8 18l-2 4"/><path d="M16 18l2 4"/>
            </svg>
          </button>
        </div>

        {/* ── Google Map ── */}
        <GoogleMap
          mapContainerStyle={MAP_CONTAINER_STYLE}
          center={getMapCenter()}
          zoom={vehicleTrackingData.length > 0 ? 12 : 7}
          options={{ ...BASE_MAP_OPTIONS, mapTypeId: mapTypes[selectedLayer] }}
          onLoad={onMapLoad}
          onClick={() => setActiveInfoWindow(null)}
        >
          {!isLoadingData && (
            <>
              {/* Completed segments — grey */}
              {segmentedRoutes.map((seg, idx) =>
                completedSegments.includes(idx) && seg.coordinates.length > 1 ? (
                  <Polyline key={`comp-${seg.id}`} path={seg.coordinates}
                    options={{ strokeColor: "#cccccc", strokeWeight: 3, strokeOpacity: 0.3 }} />
                ) : null
              )}

              {/* Active segment — blue (planned) or orange (dynamic) */}
              {currentSegmentCoordinates.length > 1 && (
                <Polyline path={currentSegmentCoordinates}
                  options={{
                    strokeColor: isSegmentDynamic ? "#f59e0b" : "#1a7a73",
                    strokeWeight: 6, strokeOpacity: 0.9,
                  }} />
              )}

              {/* Next segment preview — green dashed */}
              {nextSegmentCoordinates.length > 1 && (
                <Polyline path={nextSegmentCoordinates}
                  options={{ strokeColor: "#10b981", strokeWeight: 4, strokeOpacity: 0.6 }} />
              )}

              {/* Planned route — red faded */}
              {routeCoordinates.length > 1 && (
                <Polyline path={routeCoordinates}
                  options={{ strokeColor: "#ef4444", strokeWeight: 4, strokeOpacity: 0.4 }} />
              )}

              {/* Combined live route — cyan */}
              {combinedRoute.length > 1 && currentStatus === "IN PROGRESS" && allTrackingPoints.length > 0 && (
                <Polyline path={combinedRoute}
                  options={{ strokeColor: "#0bedf5", strokeWeight: 4, strokeOpacity: 0.95 }} />
              )}

              {/* ── Start Marker (green SVG pin) ── */}
              {routePoints.length > 0 && routePoints[0] && (
                <>
                  <Marker
                    position={{ lat: parseFloat(routePoints[0].latitude), lng: parseFloat(routePoints[0].longitude) }}
                    icon={pinIcon("green")}
                    title="Start Point"
                    onClick={() => setActiveInfoWindow("start")}
                  />
                  {activeInfoWindow === "start" && (
                    <InfoWindow
                      position={{ lat: parseFloat(routePoints[0].latitude), lng: parseFloat(routePoints[0].longitude) }}
                      onCloseClick={() => setActiveInfoWindow(null)}
                    >
                      <div style={{ minWidth: 260, maxWidth: 300, overflow: "hidden", fontFamily: "'Segoe UI', sans-serif" }}>
                        {/* Green header */}
                        <div style={{ background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)", padding: "12px 14px 10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ width: 34, height: 34, borderRadius: 8, background: "rgba(255,255,255,0.22)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5" fill="white" stroke="none"/></svg>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Start Point</div>
                            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.82)", marginTop: 1 }}>Planned starting location</div>
                          </div>
                          <button onClick={() => setActiveInfoWindow(null)} style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(255,255,255,0.25)", border: "1px solid rgba(255,255,255,0.35)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 11, fontWeight: 700, padding: 0, flexShrink: 0 }}>✕</button>
                        </div>
                        {/* Body */}
                        <div style={{ padding: "12px 14px 14px", background: "#fff" }}>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: 9, marginBottom: 10 }}>
                            <div style={{ width: 26, height: 26, borderRadius: 6, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                            </div>
                            <div>
                              <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Start Time</div>
                              <div style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{formatTrackingTime(data?.startDate)}</div>
                            </div>
                          </div>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
                            <div style={{ width: 26, height: 26, borderRadius: 6, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Location</div>
                              <div style={{ fontSize: 11, color: "#475569", background: "#f0fdf4", borderRadius: 6, padding: "6px 10px", borderLeft: "3px solid #22c55e", lineHeight: 1.5 }}>
                                {keyPointsAddresses[routePoints[0].id] || <span style={{ color: "#94a3b8", fontStyle: "italic" }}>Loading address…</span>}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </InfoWindow>
                  )}
                </>
              )}

              {/* ── Halt Markers (yellow circle SVG) ── */}
              {getHaltPoints().map((pt, idx) => (
                <React.Fragment key={pt.id}>
                  <Marker
                    position={{ lat: parseFloat(pt.latitude), lng: parseFloat(pt.longitude) }}
                    icon={haltIcon()}
                    title={`Halt ${idx + 1}`}
                    onClick={() => setActiveInfoWindow(`halt-${pt.id}`)}
                  />
                  {activeInfoWindow === `halt-${pt.id}` && (
                    <InfoWindow
                      position={{ lat: parseFloat(pt.latitude), lng: parseFloat(pt.longitude) }}
                      onCloseClick={() => setActiveInfoWindow(null)}
                    >
                      <div style={{ minWidth: 260, maxWidth: 300, overflow: "hidden", fontFamily: "'Segoe UI', sans-serif" }}>
                        {/* Amber header */}
                        <div style={{ background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)", padding: "12px 14px 10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ width: 34, height: 34, borderRadius: 8, background: "rgba(255,255,255,0.22)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><rect x="6" y="4" width="4" height="16" rx="2"/><rect x="14" y="4" width="4" height="16" rx="2"/></svg>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Halt Point {idx + 1}</div>
                            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.82)", marginTop: 1 }}>Scheduled stop location</div>
                          </div>
                          <button onClick={() => setActiveInfoWindow(null)} style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(255,255,255,0.25)", border: "1px solid rgba(255,255,255,0.35)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 11, fontWeight: 700, padding: 0, flexShrink: 0 }}>✕</button>
                        </div>
                        {/* Body */}
                        <div style={{ padding: "12px 14px 14px", background: "#fff" }}>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: 9, marginBottom: 10 }}>
                            <div style={{ width: 26, height: 26, borderRadius: 6, background: "#fffbeb", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                            </div>
                            <div>
                              <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Wait Duration</div>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>{pt.waitTime}</div>
                                <div style={{ fontSize: 10, color: "#64748b", background: "#fef3c7", borderRadius: 4, padding: "1px 6px", fontWeight: 600 }}>mins</div>
                              </div>
                            </div>
                          </div>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
                            <div style={{ width: 26, height: 26, borderRadius: 6, background: "#fffbeb", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Location</div>
                              <div style={{ fontSize: 11, color: "#475569", background: "#fffbeb", borderRadius: 6, padding: "6px 10px", borderLeft: "3px solid #f59e0b", lineHeight: 1.5 }}>
                                {keyPointsAddresses[pt.id] || <span style={{ color: "#94a3b8", fontStyle: "italic" }}>Loading address…</span>}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </InfoWindow>
                  )}
                </React.Fragment>
              ))}

              {/* ── End Marker (red SVG pin) ── */}
              {routePoints.length > 1 && routePoints[routePoints.length - 1] && (
                <>
                  <Marker
                    position={{ lat: parseFloat(routePoints[routePoints.length - 1].latitude), lng: parseFloat(routePoints[routePoints.length - 1].longitude) }}
                    icon={pinIcon("red")}
                    title="End Point"
                    onClick={() => setActiveInfoWindow("end")}
                  />
                  {activeInfoWindow === "end" && (
                    <InfoWindow
                      position={{ lat: parseFloat(routePoints[routePoints.length - 1].latitude), lng: parseFloat(routePoints[routePoints.length - 1].longitude) }}
                      onCloseClick={() => setActiveInfoWindow(null)}
                    >
                      <div style={{ minWidth: 260, maxWidth: 300, overflow: "hidden", fontFamily: "'Segoe UI', sans-serif" }}>
                        {/* Red header */}
                        <div style={{ background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)", padding: "12px 14px 10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ width: 34, height: 34, borderRadius: 8, background: "rgba(255,255,255,0.22)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5" fill="white" stroke="none"/></svg>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>End Point</div>
                            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.82)", marginTop: 1 }}>Planned ending location</div>
                          </div>
                          <button onClick={() => setActiveInfoWindow(null)} style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(255,255,255,0.25)", border: "1px solid rgba(255,255,255,0.35)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 11, fontWeight: 700, padding: 0, flexShrink: 0 }}>✕</button>
                        </div>
                        {/* Body */}
                        <div style={{ padding: "12px 14px 14px", background: "#fff" }}>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: 9, marginBottom: 10 }}>
                            <div style={{ width: 26, height: 26, borderRadius: 6, background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                            </div>
                            <div>
                              <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>End Time</div>
                              <div style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{formatTrackingTime(data?.endDate)}</div>
                            </div>
                          </div>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
                            <div style={{ width: 26, height: 26, borderRadius: 6, background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Location</div>
                              <div style={{ fontSize: 11, color: "#475569", background: "#fef2f2", borderRadius: 6, padding: "6px 10px", borderLeft: "3px solid #ef4444", lineHeight: 1.5 }}>
                                {keyPointsAddresses[routePoints[routePoints.length - 1].id] || <span style={{ color: "#94a3b8", fontStyle: "italic" }}>Loading address…</span>}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </InfoWindow>
                  )}
                </>
              )}

              {/* ── Live Vehicle Marker ── */}
              {movingVehiclePos && currentStatus === "IN PROGRESS" && allTrackingPoints.length > 0 && (
                <>
                  <Marker
                    position={movingVehiclePos}
                    icon={liveVehicleIcon(isSegmentDynamic, vehicleColorStatus, vehicleType, vehicleHeading)}
                    title="Live Vehicle"
                    zIndex={1000}
                    onClick={() => { setActiveInfoWindow("vehicle"); setShowVehiclePanel(true); }}
                  />
                  {activeInfoWindow === "vehicle" && (
                    <InfoWindow position={movingVehiclePos} onCloseClick={() => setActiveInfoWindow(null)}>
                      {(() => {
                        const spd = parseFloat(allTrackingPoints[allTrackingPoints.length - 1]?.speed || 0);
                        const spdColor = spd <= 40 ? "#22c55e" : spd <= 60 ? "#f59e0b" : "#ef4444";
                        const spdBg   = spd <= 40 ? "#f0fdf4"  : spd <= 60 ? "#fffbeb"  : "#fef2f2";
                        const hdrGrad = isSegmentDynamic
                          ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                          : "linear-gradient(135deg, #1a7a73 0%, #1a7a73 100%)";
                        const iconBg = isSegmentDynamic ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.22)";
                        const rowBg  = isSegmentDynamic ? "#fffbeb" : "#f0fdfb";
                        const rowBdr = isSegmentDynamic ? "#f59e0b" : "#1a7a73";
                        return (
                          <div style={{ minWidth: 260, maxWidth: 300, overflow: "hidden", fontFamily: "'Segoe UI', sans-serif" }}>
                            {/* Blue / orange header */}
                            <div style={{ background: hdrGrad, padding: "12px 14px 10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                              <div style={{ width: 34, height: 34, borderRadius: 8, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2"/><circle cx="6.5" cy="16.5" r="2.5"/><circle cx="16.5" cy="16.5" r="2.5"/></svg>
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Live Vehicle</div>
                                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.82)", marginTop: 1 }}>
                                  {isSegmentDynamic ? "Dynamic route active" : "Following planned route"}
                                </div>
                              </div>
                              <button onClick={() => setActiveInfoWindow(null)} style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(255,255,255,0.25)", border: "1px solid rgba(255,255,255,0.35)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 11, fontWeight: 700, padding: 0, flexShrink: 0 }}>✕</button>
                            </div>
                            {/* Body */}
                            <div style={{ padding: "12px 14px 14px", background: "#fff" }}>
                              {/* Speed row */}
                              <div style={{ display: "flex", alignItems: "flex-start", gap: 9, marginBottom: 10 }}>
                                <div style={{ width: 26, height: 26, borderRadius: 6, background: spdBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={spdColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 100 20A10 10 0 0012 2z"/><path d="M12 6v4l2.5 2.5"/><circle cx="12" cy="12" r="1.5" fill={spdColor}/></svg>
                                </div>
                                <div>
                                  <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Current Speed</div>
                                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <div style={{ fontSize: 16, fontWeight: 800, color: spdColor, lineHeight: 1 }}>{spd.toFixed(1)}</div>
                                    <div style={{ fontSize: 10, color: "#64748b", background: spdBg, borderRadius: 4, padding: "2px 6px", fontWeight: 600, border: `1px solid ${spdColor}30` }}>km/h</div>
                                  </div>
                                </div>
                              </div>
                              {/* Updated time row */}
                              <div style={{ display: "flex", alignItems: "flex-start", gap: 9, marginBottom: 10 }}>
                                <div style={{ width: 26, height: 26, borderRadius: 6, background: rowBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={rowBdr} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                </div>
                                <div>
                                  <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Last Updated</div>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{formatTrackingTime(allTrackingPoints[allTrackingPoints.length - 1]?.vehicle_tracking_time)}</div>
                                </div>
                              </div>
                              {/* Route type row */}
                              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                                <div style={{ width: 26, height: 26, borderRadius: 6, background: rowBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={rowBdr} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/></svg>
                                </div>
                                <div>
                                  <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 3 }}>Route Type</div>
                                  <div style={{ display: "inline-flex", alignItems: "center", gap: 4, background: rowBg, borderRadius: 5, padding: "2px 8px", border: `1px solid ${rowBdr}40` }}>
                                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: rowBdr }}></div>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: rowBdr }}>{isSegmentDynamic ? "Dynamic" : "Planned"}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </InfoWindow>
                  )}
                </>
              )}

              {/* ── Proximity Circles ── */}
              {Object.entries(userProximityToPoints).map(([pointId, distance]) => {
                if (typeof distance !== "number" || !isFinite(distance)) return null;
                const pt = routePoints.find((p) => p.id.toString() === pointId);
                if (!pt || distance > 0.1) return null;
                const radius = distance * 1000;
                if (!radius || radius <= 0) return null;
                return (
                  <Circle key={`prox-${pointId}`}
                    center={{ lat: parseFloat(pt.latitude), lng: parseFloat(pt.longitude) }}
                    radius={radius}
                    options={{
                      fillColor: distance <= 0.05 ? "#10b981" : "#f59e0b",
                      fillOpacity: 0.2,
                      strokeColor: distance <= 0.05 ? "#059669" : "#d97706",
                      strokeWeight: 2, strokeOpacity: 0.6, clickable: false,
                    }}
                  />
                );
              })}
            </>
          )}
          {showTransit && <TransitLayer />}
        </GoogleMap>
      </div>{/* end map-wrapper-modern */}
          </div>{/* end map card */}
          {/* ── Route Legend ── */}
          <div className="route-legend-bar">
            <div className="route-legend-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#34A853"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/></svg>
              <span>Start</span>
            </div>
            <div className="route-legend-item">
              <div className="route-legend-dot" style={{ background: "#FBBC04" }}></div>
              <span>Halt</span>
            </div>
            <div className="route-legend-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#EA4335"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/></svg>
              <span>End</span>
            </div>
            <div className="route-legend-item" style={{ marginLeft: 4 }}>
              <div className="route-legend-line" style={{ background: "#1a7a73" }}></div>
              <span>Planned Route</span>
            </div>
            <div className="route-legend-item">
              <div className="route-legend-line" style={{ background: "#f59e0b" }}></div>
              <span>Dynamic Route</span>
            </div>
            <div className="route-legend-item">
              <div className="route-legend-line" style={{ background: "#0bedf5" }}></div>
              <span>Live Route</span>
            </div>
            <div className="route-legend-item">
              <div className="route-legend-line" style={{ background: "#10b981" }}></div>
              <span>Next Segment</span>
            </div>
          </div>{/* end route-legend-bar */}
        </div>{/* end right col */}
      </div>{/* end row */}

      <style>{`
        @keyframes pulse {
          0%   { box-shadow: 0 0 0 0    rgba(245,158,11,0.7); }
          70%  { box-shadow: 0 0 0 18px rgba(245,158,11,0);   }
          100% { box-shadow: 0 0 0 0    rgba(245,158,11,0);   }
        }
        .route-type.dynamic { color:#f59e0b; font-weight:600; }
        .route-type.planned  { color:#1a7a73; font-weight:600; }
        .gm-ui-hover-effect { display: none !important; }
        .gm-style-iw-chr    { display: none !important; }
        .gm-style-iw-d      { overflow: hidden !important; padding: 0 !important; }
        .gm-style-iw-c      { padding: 12px !important; border-radius: 10px !important; box-shadow: 0 4px 16px rgba(0,0,0,0.18) !important; }
        .gm-style-iw        { padding: 0 !important; }
      `}</style>
    </div>
  );
};

export default TripAction;